import AddBoxRoundedIcon from '@mui/icons-material/AddBoxRounded';
import {Divider, Drawer, IconButton, Stack, Typography} from '@mui/material';
import Offer from '@screens/admin/audits/audits-offer/components/Offer';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

import {Close} from '@mui/icons-material';
import {useObservable} from '@ngneat/react-rxjs';
import {AuditOfferCreationDto, AuditOfferResponseDto} from '@store/audits/audit-offer/audit-offer.model';
import {auditsOfferService} from '@store/audits/audit-offer/audit-offer.services';
import {auditsofferQuery} from '@store/audits/audit-offer/audits-offer.query';
import {ServiceKindTypeEnum} from '@store/settings/serviceKinds';
import _ from 'lodash';
import {useSnackbar} from 'notistack';
import {useEffect, useState} from 'react';
import {switchMap} from 'rxjs';

export type keyOfAuditOfferResponseData = keyof AuditOfferResponseDto;

const AddOfferDrawer = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { auditId } = useParams();
    const { state: { operationId } } = useLocation() as { state: { operationId: number } };
    const [{ auditsOffer }] = useObservable(auditsofferQuery.auditsofferList$);
    const offersInitValue = auditsOffer.find(ofr => ofr.auditOperationId === operationId)?.auditOffer as Partial<AuditOfferResponseDto[]>;
    const [offers, setOffers] = useState<Partial<AuditOfferResponseDto[]>>(offersInitValue);

    const [version] = useObservable(auditsofferQuery.version$);


    const handleClose = () => navigate(-1);

    useEffect(() => {
        if (_.isUndefined(offers)) handleClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offers]);

    function handleAddOffer() {
        const newData: Partial<AuditOfferResponseDto> = {
            type: ServiceKindTypeEnum.PICKUP,
            unitPriceInCents: 0,
            comment: '',
        };

        setOffers([...offers, { ...newData as AuditOfferResponseDto }]);
    }

    const updateAuditOffers = (
        index: number,
        field: keyOfAuditOfferResponseData,
        value: AuditOfferResponseDto[keyOfAuditOfferResponseData]
    ) => {
        setOffers((prevAuditOffers) => {
            const updatedOffers = [...prevAuditOffers];
            const updatedOffer = { ...updatedOffers[index], [field]: value };
            updatedOffers[index] = updatedOffer as AuditOfferResponseDto;
            return updatedOffers;
        });
    };

    const deleteAuditOffer = (idx: number, auditOffer: Partial<AuditOfferResponseDto>) => {
        setOffers((prevAuditOffers) => {
            const updatedOffers = [...prevAuditOffers];
            updatedOffers.splice(idx, 1);
            return updatedOffers;
        });
        if (auditOffer.id && auditId && version) {
            auditsOfferService.deleteAuditOffer(auditOffer.id).pipe(
                switchMap(() => auditsOfferService.getAuditOffer(auditId, version))
            ).subscribe({
                error: (err) => enqueueSnackbar(err.text, err.options),
                next: () => {
                    enqueueSnackbar(t('audits.success.offer.delete'), { variant: 'success' });
                },
            });
        }
    };

    const createOffer = (offer: Partial<AuditOfferResponseDto>) => {
        const createdOfferData: AuditOfferCreationDto = {
            ...offer as AuditOfferCreationDto,
            auditOfferId: offer?.id,
            auditOperationId: operationId,
        };
        createdOfferData.unitPriceInCents = Number(createdOfferData.unitPriceInCents);

        if (auditId && version) {
            auditsOfferService.createAuditOffer(createdOfferData).pipe(
                switchMap(() => auditsOfferService.getAuditOffer(auditId, version))
            ).subscribe({
                error: (err) => enqueueSnackbar(err.text, err.options),
                next: () => {
                    enqueueSnackbar(t(`audits.success.offer.${offer?.id ? 'update' : 'create'}`), { variant: 'success' });
                },
            });
        }
    };

    return (
        <Drawer PaperProps={{ sx: { paddingRight: 0, paddingBottom: 0 } }} anchor="right" open onClose={handleClose}>
            <Stack pr="20px">
                <Stack direction="row" justifyContent="flex-end">
                    <IconButton onClick={handleClose}>
                        <Close />
                    </IconButton>
                </Stack>
                <Stack spacing={0.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb="15px" spacing={1.5}>
                        <Typography fontSize={24} fontWeight="700">
                            {t('audits.audit-offer.title')}
                        </Typography>
                        <IconButton onClick={handleAddOffer}>
                            <AddBoxRoundedIcon />
                        </IconButton>
                    </Stack>
                    <Divider variant="fullWidth" orientation="horizontal" />
                </Stack>
            </Stack>
            <Stack className="scrollable" overflow="auto" spacing={2} p={'15px 14px 12px 0'} mr="8px" height={'100vh'}>
                {offers?.length > 0 && offers.map((offer, index) =>
                    <Offer
                        key={index}
                        index={index}
                        auditOffer={offer}
                        updateAuditOffer={updateAuditOffers}
                        deleteAuditOffer={deleteAuditOffer}
                        createOffer={createOffer} />
                )}
            </Stack>
        </Drawer >
    );
};

export default AddOfferDrawer;
